import MuiBadge from "@common/components/Badge";
import { BadgeOwnProps, Box, Stack, styled, Theme } from "@mui/material";
import React from "react";

type DeviceImageBadge = {
  icon: React.ReactNode;
  color?: "white" | "green" | "orange" | "red" | "unknown";
};
export type DeviceCardImageProps = {
  image?: string;
  topLeftBadge?: DeviceImageBadge;
  topRightBadge?: DeviceImageBadge;
  bottomLeftBadge?: DeviceImageBadge;
  bottomRightBadge?: DeviceImageBadge;
};

const Badge = styled(MuiBadge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.background.default,
  },
}));
const StyledImg = styled("img")(({ theme }) => ({
  width: theme.spacing(10),
  height: "auto",
  borderRadius: theme.spacing(2),
  objectFit: "contain",
}));
const getColorFn = (theme: Theme) => (color?: DeviceImageBadge["color"]) => {
  switch (color) {
    case "white":
      return theme.palette.common.white;
    case "green":
      return theme.palette.success.light;
    case "orange":
      return theme.palette.warning.main;
    case "red":
      return theme.palette.error.main;
    case "unknown":
      return theme.palette.common.grey.g9B9B9B;
    default:
      return theme.palette.common.white;
  }
};
const WithBadge = ({
  children,
  position,
  anchorOrigin,
  ...props
}: React.PropsWithChildren<
  DeviceCardImageProps &
    Required<Pick<BadgeOwnProps, "anchorOrigin">> & {
      position: keyof Omit<DeviceCardImageProps, "image">;
    }
>) => {
  const badgeDef = props[position];
  if (badgeDef === undefined) return <>{children}</>;
  const { icon, color } = badgeDef;
  const { vertical, horizontal } = anchorOrigin;
  return (
    <Badge
      badgeContent={icon}
      anchorOrigin={anchorOrigin}
      sx={(theme) => ({
        color: getColorFn(theme)(color),
      })}
    >
      {children}
    </Badge>
  );
};

const DeviceCardImage = (
  props: React.PropsWithChildren<DeviceCardImageProps>
) => {
  const { image, ...other } = props;
  const img = Boolean(image) ? image : undefined;

  return (
    <Stack
      direction="column"
      sx={[
        {
          justifyContent: "center",
        },
        (theme) => ({ height: theme.spacing(10) }),
      ]}
    >
      <WithBadge
        {...other}
        image={img}
        position="topLeftBadge"
        anchorOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <WithBadge
          {...other}
          image={img}
          position="topRightBadge"
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <WithBadge
            {...other}
            image={img}
            position="bottomLeftBadge"
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <WithBadge
              {...other}
              image={img}
              position="bottomRightBadge"
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
            >
              <StyledImg src={img} />
            </WithBadge>
          </WithBadge>
        </WithBadge>
      </WithBadge>
    </Stack>
  );
};

export default DeviceCardImage;
